/**
 * PluginActivityRouter - React 19.2 Activity-based Plugin State Preservation
 * 
 * This component wraps the DynamicPluginRouter to provide:
 * 1. State preservation when switching between plugins
 * 2. Instant navigation to previously visited plugins
 * 3. Background updates for hidden plugins (deferred by React)
 * 
 * Instead of unmounting plugins when navigating away, we hide them
 * using <Activity mode="hidden">. This preserves:
 * - Form inputs and user data
 * - Scroll positions
 * - Component state (useState, useReducer)
 * - Query cache state
 * 
 * @see https://react.dev/reference/react/Activity
 * @since React 19.2
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { loadPlugin, getPluginManifestById } from './registry';
import { PluginProvider } from './PluginContext';
import { PluginErrorBoundary } from './ErrorBoundary';
import { AccessDeniedError, type DenialContext } from './AccessDeniedError';
import { PluginLoadingUI, type LoadingStep } from './PluginLoadingUI';
import { createPluginLoadingSteps, markStepError } from './pluginLoadingSteps';
import { getAuthzenSubject } from '../core/auth/subject';
import { buildPluginRouteEvaluation } from '../core/auth/authz-operation-refs';
import { postRouteEvaluations, PDP_EVAL_TIMEOUT_MS } from './pdpRouteEval';
import { isPublicRoute, isManifestPublicPath } from '../core/auth/publicRoutes';
import { useQuickAccess } from '../contexts/QuickAccessContext';
import type { PluginManifest, LoadedPlugin } from './types';
import { Class2PluginFrame } from '../core/plugins/Class2PluginFrame';
import { PluginPageRoot } from '../core/layout/shellPluginPageRoot';
// Import shared SDK readiness promise to prevent race conditions
import { pluginAuthReady } from '../main';

function _pa() { return (globalThis as any).PluginAuth; }

function SystemReadyGate({ children }: { children: React.ReactNode }) {
	const useSystemSelection = _pa()?.useSystemSelection;
	const ctx = useSystemSelection?.();
	if (!ctx || ctx.loading) return null;
	if (ctx.activeSystem === null && !ctx.degraded && !ctx.error) return null;
	return <React.Fragment key={ctx.activeSystem}>{children}</React.Fragment>;
}

/**
 * Performs a client-side redirect via react-router navigate().
 * Used by manifest routes that declare `redirect_to` instead of `component`.
 */
function RouteRedirector({ to }: { to: string }) {
	const navigate = useNavigate();
	useEffect(() => {
		navigate(to, { replace: true });
	}, [navigate, to]);
	return null;
}

/**
 * State for a mounted plugin in the Activity cache
 */
interface MountedPlugin {
  pluginId: string;
  plugin: LoadedPlugin;
  manifest: PluginManifest;
  routes: any[];
  authorizedRoutes: Set<string>;
  denialContexts: Map<string, DenialContext>;
  mountedAt: number;
  lastAccessedAt: number;
}

// Module-level cache for mounted plugins
const mountedPluginsCache = new Map<string, MountedPlugin>();

/**
 * Module-level set to track plugins currently being loaded.
 * 
 * This prevents the race condition where useEffect re-runs during async mount:
 * 1. User navigates to /agent-designer
 * 2. useEffect calls mountPlugin() (async)
 * 3. State updates during loading trigger re-render
 * 4. useEffect re-runs before mount completes
 * 5. Without this guard, second mountPlugin() would start → duplicate API calls
 * 
 * The Set is checked synchronously at mount start and cleared on completion/error.
 * Using module-level Set (not React state) ensures synchronous updates.
 * 
 * @see https://react.dev/reference/react/useEffect#fetching-data-with-effects
 */
const loadingPluginsSet = new Set<string>();

/** Metrics for observability - tracks mount race condition prevention */
const mountMetrics = {
  duplicatesPrevented: 0,
  totalMounts: 0,
  lastPreventedAt: null as number | null,
};

// Maximum number of plugins to keep mounted
const MAX_MOUNTED_PLUGINS = 6;

function extractRequestableRoles(
  manifest: PluginManifest | undefined,
  pluginId: string,
  routePath: string
): string[] {
  const resources = (manifest as any)?.authorization?.resources;
  if (!Array.isArray(resources)) {
    return [];
  }
  const routeIds = new Set<string>([
    `route:${pluginId}:${routePath}`,
    `route:${pluginId}:${routePath.replace(/\/+$/, '')}`,
  ]);
  const pluginIds = new Set<string>([
    `plugin:${pluginId}`,
    `plugin:${pluginId}:root`,
  ]);
  const roles = new Set<string>();
  for (const resource of resources) {
    const protection = resource?.protection;
    const requiredRoles = Array.isArray(protection?.required_roles) ? protection.required_roles : [];
    if (requiredRoles.length === 0) {
      continue;
    }
    const id = String(resource?.id || '');
    const type = String(resource?.type || '');
    const isRouteMatch = type === 'experience.route' && routeIds.has(id);
    const isPluginMatch = type === 'experience.plugin' && pluginIds.has(id);
    if (!isRouteMatch && !isPluginMatch) {
      continue;
    }
    requiredRoles.forEach((role: string) => roles.add(role));
  }
  return Array.from(roles);
}

/**
 * Evict least recently used plugins when cache is full
 */
function evictLRUPlugin(): void {
  if (mountedPluginsCache.size < MAX_MOUNTED_PLUGINS) return;

  let oldestKey: string | null = null;
  let oldestTime = Infinity;

  for (const [key, plugin] of mountedPluginsCache.entries()) {
    if (plugin.lastAccessedAt < oldestTime) {
      oldestTime = plugin.lastAccessedAt;
      oldestKey = key;
    }
  }

  if (oldestKey) {
    console.log(`[PluginActivityRouter] Evicting LRU plugin: ${oldestKey}`);
    mountedPluginsCache.delete(oldestKey);
  }
}

/**
 * Get the plugin ID from the current path (simple extraction)
 */
function getPluginIdFromPath(pathname: string): string | null {
  const parts = pathname.split('/').filter(Boolean);
  return parts[0] || null;
}

/**
 * Check if a route path pattern matches a given pathname.
 * Handles both static routes and dynamic routes with :params.
 */
function routeMatchesPath(routePath: string, pathname: string): boolean {
  // Exact match
  if (routePath === pathname) return true;
  
  // Dynamic route match ( e.g., /agents/:agentId matches /agents/123)
  if (routePath.includes(':')) {
    const pattern = routePath.replace(/:[^/]+/g, '[^/]+');
    return new RegExp(`^${pattern}$`).test(pathname);
  }
  
  // Prefix match for nested routes
  return pathname.startsWith(routePath + '/');
}

/**
 * Resolve the manifest route entry for a pathname.
 * Prefer exact > parameterized > longest static prefix so /nomadid/platform-users
 * does not collapse to the /nomadid dashboard route.
 */
function findAttemptedRoute(routes: any[], currentPath: string): any | undefined {
  const exact = routes.find((r) => r.path === currentPath);
  if (exact) return exact;

  const parameterized = routes.find(
    (r) => r.path.includes(':') && routeMatchesPath(r.path, currentPath),
  );
  if (parameterized) return parameterized;

  const prefixMatches = routes
    .filter((r) => !r.path.includes(':') && currentPath.startsWith(`${r.path}/`))
    .sort((a, b) => b.path.length - a.path.length);

  return prefixMatches[0];
}

/**
 * Check if a plugin owns routes for a given path segment.
 * Returns true if any route in the plugin starts with /{pathSegment}
 */
function pluginOwnsPathSegment(routes: Array<{ path: string }>, pathSegment: string): boolean {
  const prefix = `/${pathSegment}`;
  return routes.some(r => r.path === prefix || r.path.startsWith(prefix + '/') || r.path.startsWith(prefix + ':'));
}

/**
 * Resolve URL path to actual plugin ID by checking which plugin owns the route.
 * This handles cases where plugin ID differs from route prefix (e.g., "agent-hub" owns "/agents").
 */
function resolvePluginIdFromPath(
  pathname: string, 
  manifests: Map<string, PluginManifest>
): string | null {
  const pathSegment = getPluginIdFromPath(pathname);
  if (!pathSegment) return null;

  console.log(`[resolvePluginIdFromPath] pathname=${pathname}, pathSegment=${pathSegment}, manifests.size=${manifests.size}`);
  
  // Debug: Log all manifests with primary_route to help diagnose routing issues
  const manifestsWithPrimaryRoute = Array.from(manifests.values()).filter(m => m.primary_route);
  if (manifestsWithPrimaryRoute.length > 0) {
    console.log(`[resolvePluginIdFromPath] Manifests with primary_route:`, manifestsWithPrimaryRoute.map(m => ({ id: m.id, primary_route: m.primary_route })));
  } else {
    console.warn(`[resolvePluginIdFromPath] NO manifests have primary_route! Sample manifest:`, Array.from(manifests.values())[0]);
  }

  // First check mounted plugins (already loaded, faster lookup)
  for (const [pluginId, mounted] of mountedPluginsCache.entries()) {
    const ownsRoute = mounted.routes.some(r => routeMatchesPath(r.path, pathname));
    const ownsSegment = pluginOwnsPathSegment(mounted.routes, pathSegment);
    if (ownsRoute || ownsSegment) {
      console.log(`[resolvePluginIdFromPath] Found in mounted cache: ${pluginId}`);
      return pluginId;
    }
  }

  // Then check manifests for plugins not yet mounted
  for (const manifest of manifests.values()) {
    // Direct ID match
    if (manifest.id === pathSegment) {
      console.log(`[resolvePluginIdFromPath] Direct ID match: ${manifest.id}`);
      return manifest.id;
    }
    
    // Primary route match - check if pathname equals or is nested under primary_route.
    // This handles multi-segment routes like /admin/system-tester.
    // IMPORTANT: match on a path-segment boundary, NOT a bare string prefix, so a
    // plugin whose primary_route is a string-prefix of another's (e.g. "/dc-ui" vs
    // "/dc-ui-mini") does not hijack the longer path.
    if (manifest.primary_route && (pathname === manifest.primary_route || pathname.startsWith(manifest.primary_route + '/'))) {
      console.log(`[resolvePluginIdFromPath] primary_route match: ${manifest.id} (${manifest.primary_route})`);
      return manifest.id;
    }
    
    // Legacy single-segment primary route match
    if (manifest.primary_route === `/${pathSegment}`) {
      console.log(`[resolvePluginIdFromPath] Legacy primary_route match: ${manifest.id}`);
      return manifest.id;
    }
    
    // Check if pathname matches or starts with any contributed route path
    // This handles cases like /admin/system-tester where first segment isn't plugin ID
    const routes = manifest.contributions?.routes;
    if (routes) {
      for (const route of routes) {
        // Exact route match or pathname starts with route path
        if (pathname === route.path || pathname.startsWith(route.path + '/')) {
          console.log(`[resolvePluginIdFromPath] Route path match: ${manifest.id} (${route.path})`);
          return manifest.id;
        }
        // Handle parameterized routes: /admin/system-tester/systems/:name
        if (route.path.includes(':')) {
          const baseRoute = route.path.split('/:')[0];
          if (pathname === baseRoute || pathname.startsWith(baseRoute + '/')) {
            console.log(`[resolvePluginIdFromPath] Parameterized route match: ${manifest.id} (${baseRoute})`);
            return manifest.id;
          }
        }
      }
      // Also check using original pluginOwnsPathSegment for compatibility
      if (pluginOwnsPathSegment(routes, pathSegment)) {
        console.log(`[resolvePluginIdFromPath] pluginOwnsPathSegment match: ${manifest.id}`);
        return manifest.id;
      }
    }
  }

  // Fall back to path segment (may be a direct plugin ID match)
  console.log(`[resolvePluginIdFromPath] No match found, falling back to pathSegment: ${pathSegment}`);
  return pathSegment;
}

/**
 * PluginActivityRouter - Main component for Activity-based plugin routing
 */
// Try to get React 19 Activity API from multiple possible exports
const ReactAny = React as any;
const ActivityComponent = (
  ReactAny.Activity ||           // React 19.2+ stable
  ReactAny.unstable_Activity ||  // React 19 experimental
  ReactAny.experimental_Activity // React canary
) as React.ComponentType<{ mode: 'visible' | 'hidden'; children: React.ReactNode }> | undefined;

const hasActivityAPI = typeof ActivityComponent === 'function';

// Log Activity availability once
if (typeof window !== 'undefined' && !(window as any).__activityLogged) {
  console.log(`[PluginActivityRouter] React.Activity available: ${hasActivityAPI}`);
  console.log(`[PluginActivityRouter] React version: ${React.version}`);
  console.log(`[PluginActivityRouter] Available React exports with 'Activity':`, 
    Object.keys(ReactAny).filter(k => k.toLowerCase().includes('activity'))
  );
  (window as any).__activityLogged = true;
}

/** Visible host must be a full-width flex column — not `display: contents`. */
export function activityHostVisibleStyle(): React.CSSProperties {
  return {
    width: '100%',
    alignSelf: 'stretch',
    minWidth: 0,
    flex: '1 1 0',
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
  };
}

/** Hidden host keeps the subtree mounted but out of layout. */
export function activityHostHiddenStyle(): React.CSSProperties {
  return {
    visibility: 'hidden',
    position: 'absolute',
    width: 0,
    height: 0,
    overflow: 'hidden',
    pointerEvents: 'none',
  };
}

/**
 * ActivityWrapper - Wraps plugin content for state preservation
 *
 * Uses React.Activity when available (nested inside a full-width host).
 * The host is always a block-level flex child of `.emp-main`. Do not use
 * `display: contents` here: hoisting plugin roots into the flex container
 * makes `margin: 0 auto` + `max-width` shrink-wrap to content (~card width).
 */
function ActivityWrapper({
  mode,
  pluginId,
  children,
}: {
  mode: 'visible' | 'hidden';
  pluginId: string;
  children: React.ReactNode;
}) {
  const hostStyle = mode === 'visible' ? activityHostVisibleStyle() : activityHostHiddenStyle();

  return (
    <div
      data-plugin-activity={pluginId}
      data-activity-mode={mode}
      style={hostStyle}
      aria-hidden={mode === 'hidden'}
    >
      {hasActivityAPI && ActivityComponent ? (
        <ActivityComponent mode={mode}>{children}</ActivityComponent>
      ) : (
        children
      )}
    </div>
  );
}

export function PluginActivityRouter({
  manifests,
  debug = false,
}: {
  manifests: Map<string, PluginManifest>;
  debug?: boolean;
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { trackAccess } = useQuickAccess();
  
  const [mountedPlugins, setMountedPlugins] = useState<Map<string, MountedPlugin>>(
    () => new Map(mountedPluginsCache)
  );
  const [loadingPlugin, setLoadingPlugin] = useState<string | null>(null);
  const [loadingSteps, setLoadingSteps] = useState<LoadingStep[]>([]);
  const [error, setError] = useState<{ pluginId: string; error: Error } | null>(null);
  const [disabledPlugin, setDisabledPlugin] = useState<string | null>(null);
  const [deniedPlugin, setDeniedPlugin] = useState<string | null>(null);

  // Resolve URL path to actual plugin ID (handles cases like "/agents" → "agent-hub")
  const activePluginId = useMemo(
    () => resolvePluginIdFromPath(location.pathname, manifests), 
    [location.pathname, manifests]
  );

  const log = useCallback((...args: any[]) => {
    if (debug) {
      console.log('[PluginActivityRouter]', ...args);
    }
  }, [debug]);

  // Expose navigate function globally for plugins
  useEffect(() => {
    if (typeof window !== 'undefined') {
      (window as any).__pluginNavigate = (path: string) => {
        if (typeof path === 'string' && path.startsWith('/')) {
          navigate(path);
        }
      };
    }
    return () => {
      if (typeof window !== 'undefined') {
        delete (window as any).__pluginNavigate;
      }
    };
  }, [navigate]);

  // Load and mount a plugin
  const mountPlugin = useCallback(async (pluginId: string): Promise<MountedPlugin | null> => {
    // Check if already mounted
    const existing = mountedPluginsCache.get(pluginId);
    if (existing) {
      existing.lastAccessedAt = Date.now();
      return existing;
    }

    // Check if already loading (prevents race condition with duplicate mounts)
    // This guard is critical - see JSDoc on loadingPluginsSet for details
    if (loadingPluginsSet.has(pluginId)) {
      mountMetrics.duplicatesPrevented++;
      mountMetrics.lastPreventedAt = Date.now();
      
      console.warn(
        `[PluginActivityRouter] Race condition prevented: plugin "${pluginId}" mount already in progress`,
        {
          pluginId,
          duplicatesPrevented: mountMetrics.duplicatesPrevented,
          totalMounts: mountMetrics.totalMounts,
        }
      );
      return null;
    }

    // Mark as loading BEFORE any async operations (synchronous - critical for race prevention)
    loadingPluginsSet.add(pluginId);
    mountMetrics.totalMounts++;

    log(`Mounting plugin: ${pluginId}`);
    setLoadingPlugin(pluginId);
    setLoadingSteps(createPluginLoadingSteps(true));

    try {
      // Step 0: Ensure shared SDKs are ready (prevents race condition with globalThis.PluginAuth)
      // This is critical - plugins access globalThis.SharedAuth/PluginAuth for authorization hooks
      await pluginAuthReady;
      setLoadingSteps(prev => prev.map(s => 
        s.id === 'sdk' ? { ...s, status: 'complete' } : 
        s.id === 'manifest' ? { ...s, status: 'loading' } : s
      ));

      // Step 1: Get manifest
      const manifest = await getPluginManifestById(pluginId);
      if (!manifest) {
        throw new Error(`Manifest not found for ${pluginId}`);
      }

      if (manifest.enabled === false) {
        loadingPluginsSet.delete(pluginId);
        setDisabledPlugin(pluginId);
        setLoadingPlugin(null);
        return null;
      }

      setLoadingSteps(prev => prev.map(s => 
        s.id === 'manifest' ? { ...s, status: 'complete' } : 
        s.id === 'bundle' ? { ...s, status: 'loading' } : s
      ));

      // Step 2: Load plugin bundle
      const plugin = await loadPlugin(pluginId, queryClient);
      if (!plugin) {
        throw new Error(`Failed to load plugin: ${pluginId}`);
      }

      setLoadingSteps(prev => prev.map(s => 
        s.id === 'bundle' ? { ...s, status: 'complete' } : 
        s.id === 'routes' ? { ...s, status: 'loading' } : s
      ));

      // Step 3: Extract routes
      const routes = manifest.contributions?.routes || [];
      
      setLoadingSteps(prev => prev.map(s => 
        s.id === 'routes' ? { ...s, status: 'complete' } : 
        s.id === 'authorization' ? { ...s, status: 'loading' } : s
      ));

      // Step 4: Authorization check
      const authorizedRoutes = new Set<string>();
      const denialContexts = new Map<string, DenialContext>();
      
      if (routes.length > 0) {
        // On public routes (e.g. signup), skip PDP entirely — no session/subject exists
        const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
        const onPublicRoute =
          isPublicRoute(pathname) || isManifestPublicPath(pathname, routes);
        if (onPublicRoute) {
          routes.forEach(r => authorizedRoutes.add(r.path));
        } else {
        try {
          let subject;
          try {
            subject = await getAuthzenSubject();
          } catch (err) {
            // FAIL CLOSED NOISILY: Identity governance requires known subject for all authz checks
            console.error('[IGA:AUTHZ] PluginActivityRouter: Subject resolution failed — failing closed.', err);
            subject = null;
          }
          if (subject) {
            const baseContext = manifest.authorization?.context || {};

            const routePdpApp = manifest.authorization?.pdp_application || pluginId;
            const evals = routes
              .filter(r => r.action && !(r as any).public)
              .map(r => {
                const { resource, action, context } = buildPluginRouteEvaluation(
                  pluginId,
                  routePdpApp,
                  r as any,
                  baseContext,
                );
                return { subject, resource, action, context };
              });

            if (evals.length > 0) {
              // Bounded, fail-closed PDP call (shared with DynamicRoutes).
              const outcome = await postRouteEvaluations(evals, PDP_EVAL_TIMEOUT_MS);

              if (outcome.ok && outcome.evaluations) {
                const evaluations = outcome.evaluations as any[];
                const batchRequestId = outcome.requestId;

                let evalIndex = 0;
                routes.forEach((r) => {
                  if ((r as any).public) {
                    authorizedRoutes.add(r.path);
                  } else if (r.action) {
                    const evaluation = evaluations[evalIndex];
                    const decision = evaluation?.decision;
                    if (decision === true || decision === 'Permit' || decision === 'permit') {
                      authorizedRoutes.add(r.path);
                    } else {
                      const ctx = evaluation?.context;
                      denialContexts.set(r.path, {
                        decisionId: ctx?.decision_id || ctx?.id || undefined,
                        correlationId: ctx?.extended?.correlation_id || batchRequestId || undefined,
                        reasonUser: ctx?.reason_user?.en || undefined,
                        evaluatedAt: ctx?.extended?.timestamp || undefined,
                      });
                    }
                    evalIndex++;
                  } else {
                    authorizedRoutes.add(r.path);
                  }
                });
              } else if (outcome.status === 401) {
                if (!isPublicRoute()) {
                  const interceptor = (globalThis as any).__authInterceptor;
                  if (interceptor?.handleUnauthorized && outcome.response) {
                    await interceptor.handleUnauthorized(outcome.response);
                  } else {
                    const returnTo = encodeURIComponent(window.location.pathname + window.location.search);
                    window.location.href = `/auth/login?return_to=${returnTo}`;
                  }
                }
              } else {
                // SECURITY: fail closed - PDP unavailable/unexpected response/timeout => deny routes that require auth.
                console.warn(`[IGA:AUTHZ] PluginActivityRouter: PDP evaluation failed (${outcome.status}) for ${pluginId} — failing closed.`);
              }
            } else {
              routes.forEach(r => authorizedRoutes.add(r.path));
            }
          }
        } catch (err) {
          // FAIL CLOSED NOISILY: a PDP error, network stall, or the AbortSignal.timeout
          // above lands here. Deny routes that require auth and let the loader finish —
          // never leave "Checking permissions" spinning.
          console.error(`[IGA:AUTHZ] PluginActivityRouter: PDP evaluation errored/timed out for ${pluginId} — failing closed.`, err);
        }
        }
      }

      setLoadingSteps(prev => prev.map(s => 
        s.id === 'authorization' ? { ...s, status: 'complete' } : s
      ));

      // Evict LRU if needed
      evictLRUPlugin();

      // Create mounted plugin state
      const mounted: MountedPlugin = {
        pluginId,
        plugin,
        manifest,
        routes,
        authorizedRoutes,
        denialContexts,
        mountedAt: Date.now(),
        lastAccessedAt: Date.now(),
      };

      // Update caches
      mountedPluginsCache.set(pluginId, mounted);
      loadingPluginsSet.delete(pluginId);
      setMountedPlugins(new Map(mountedPluginsCache));
      setLoadingPlugin(null);

      log(`✅ Mounted plugin: ${pluginId} with ${routes.length} routes`);
      return mounted;

    } catch (err: any) {
      console.error(`[PluginActivityRouter] Error mounting ${pluginId}:`, err);
      loadingPluginsSet.delete(pluginId);
      setLoadingSteps((prev) => markStepError(prev));
      setError({ pluginId, error: err });
      return null;
    }
  }, [queryClient, log]);

  // Helper to get the primary navigation path for a plugin
  // Uses primary_route from manifest if available, otherwise /${pluginId}
  const getPrimaryPath = useCallback((manifest: PluginManifest, pluginId: string): string => {
    const primaryRoute = (manifest as any)['primary_route'];
    if (typeof primaryRoute === 'string' && primaryRoute.startsWith('/') && primaryRoute.length > 1) {
      return primaryRoute;
    }
    // Fall back to plugin ID as the path
    return `/${pluginId}`;
  }, []);

  // Mount active plugin if not already mounted
  useEffect(() => {
    console.log(`[PluginActivityRouter] Navigation to: ${activePluginId}, cached: ${mountedPluginsCache.has(activePluginId || '')}, mounted state size: ${mountedPlugins.size}`);
    
    if (!activePluginId) return;
    
    if (mountedPluginsCache.has(activePluginId)) {
      const plugin = mountedPluginsCache.get(activePluginId)!;
      // Stale mount from legacy manifests (empty contributions.routes) — remount.
      if (plugin.routes.length === 0) {
        mountedPluginsCache.delete(activePluginId);
        setMountedPlugins((prev) => {
          const next = new Map(prev);
          next.delete(activePluginId);
          return next;
        });
      } else {
      plugin.lastAccessedAt = Date.now();
      console.log(`[PluginActivityRouter] ✅ Cache hit for ${activePluginId}, total cached: ${mountedPluginsCache.size}`);
      
      // Track access for Quick Access sidebar - use primary path for navigation
      const primaryPath = getPrimaryPath(plugin.manifest, activePluginId);
      trackAccess(activePluginId, {
        title: plugin.manifest.title || plugin.manifest.name || activePluginId,
        path: primaryPath,
        icon: plugin.manifest.icon,
        group: plugin.manifest.group,
      });
      
      // Ensure React state is synced with module cache (in case component re-mounted)
      if (!mountedPlugins.has(activePluginId)) {
        console.log(`[PluginActivityRouter] Syncing cache to React state`);
        setMountedPlugins(new Map(mountedPluginsCache));
      }
      return;
      }
    }

    // Check if this is a known plugin
    // Match by: plugin ID, primary_route, or any route path starting with the activePluginId
    const manifest = Array.from(manifests.values()).find(m => 
      m.id === activePluginId || 
      m.primary_route === `/${activePluginId}` ||
      m.contributions?.routes?.some(r => r.path.startsWith(`/${activePluginId}`))
    );

    if (manifest) {
      console.log(`[PluginActivityRouter] Loading new plugin: ${manifest.id}`);
      mountPlugin(manifest.id).then((mounted) => {
        // Track access after successful mount - use primary path for navigation
        if (mounted) {
          const primaryPath = getPrimaryPath(manifest, manifest.id);
          trackAccess(manifest.id, {
            title: manifest.title || manifest.name || manifest.id,
            path: primaryPath,
            icon: manifest.icon,
            group: manifest.group,
          });
        }
      });
    } else {
      console.log(`[PluginActivityRouter] No manifest found for: ${activePluginId}`);
    }
  }, [activePluginId, manifests, mountPlugin, mountedPlugins, trackAccess, getPrimaryPath]);

  const extractRouteParams = useCallback((routePattern: string, resolvedPath: string): Record<string, string> => {
    const params: Record<string, string> = {};
    if (!routePattern.includes(':')) return params;
    const patternSegments = routePattern.split('/').filter(Boolean);
    const pathSegments = resolvedPath.split('/').filter(Boolean);
    if (patternSegments.length !== pathSegments.length) return params;
    for (let i = 0; i < patternSegments.length; i += 1) {
      if (!patternSegments[i].startsWith(':')) continue;
      const key = patternSegments[i].slice(1);
      try {
        params[key] = decodeURIComponent(pathSegments[i] ?? '');
      } catch {
        params[key] = pathSegments[i] ?? '';
      }
    }
    return params;
  }, []);

  // Get matched route for a plugin
  const getMatchedRoute = useCallback((
    mounted: MountedPlugin,
    currentPath: string
  ): { route: any; Component: React.ComponentType; params: Record<string, string>; redirectTo?: string } | null => {
    const { plugin, routes, authorizedRoutes } = mounted;

    // Find matching route
    const matchedRoute = routes.find(r => currentPath === r.path) ||
      routes.find(r => {
        if (r.path.includes(':')) {
          const pattern = r.path.replace(/:[^/]+/g, '[^/]+');
          return new RegExp(`^${pattern}$`).test(currentPath);
        }
        return false;
      }) ||
      routes.find(r => !r.path.includes(':') && currentPath.startsWith(r.path + '/'));

    if (!matchedRoute) {
      // Try first route as fallback
      if (routes.length > 0 && currentPath === `/${mounted.pluginId}`) {
        const firstRoute = routes[0];
        const Component = plugin.routes?.[firstRoute.component];
        if (Component) {
          return { route: firstRoute, Component, params: extractRouteParams(firstRoute.path, currentPath) };
        }
      }
      return null;
    }

    // Handle redirect routes — resolve :param tokens and return target URL
    if (matchedRoute.redirect_to) {
      const params = extractRouteParams(matchedRoute.path, currentPath);
      let redirectTo: string = matchedRoute.redirect_to;
      for (const [key, value] of Object.entries(params)) {
        redirectTo = redirectTo.replace(`:${key}`, encodeURIComponent(value));
      }
      return { route: matchedRoute, Component: null as any, params, redirectTo };
    }

    // Check authorization
    if (matchedRoute.resource && matchedRoute.action && !authorizedRoutes.has(matchedRoute.path)) {
      return null; // Will show access denied
    }

    const Component = plugin.routes?.[matchedRoute.component];
    if (!Component) return null;

    return { route: matchedRoute, Component, params: extractRouteParams(matchedRoute.path, currentPath) };
  }, [extractRouteParams]);

  const activeManifest =
    manifests.get(activePluginId!) ||
    Array.from(manifests.values()).find((m) => m.id === activePluginId);

  const showLoadingSurface =
    activePluginId &&
    (loadingPlugin === activePluginId || error?.pluginId === activePluginId);

  const handleRetryMount = useCallback(() => {
    if (!activePluginId) return;
    setError(null);
    setLoadingSteps(createPluginLoadingSteps(true));
    mountPlugin(activePluginId);
  }, [activePluginId, mountPlugin]);

  const handleBackHome = useCallback(() => {
    setError(null);
    setLoadingPlugin(null);
    navigate('/');
  }, [navigate]);

  // Render loading / failed-mount state
  if (showLoadingSurface) {
    const loadFailed = error?.pluginId === activePluginId;
    return (
      <PluginLoadingUI
        pluginId={activePluginId!}
        pluginName={activeManifest?.title || activePluginId!}
        iconKey={activeManifest?.icon}
        steps={loadingSteps}
        errorMessage={loadFailed ? error!.error.message : undefined}
        onRetry={loadFailed ? handleRetryMount : undefined}
        onBack={loadFailed ? handleBackHome : undefined}
      />
    );
  }

  // Render disabled error
  if (disabledPlugin === activePluginId) {
    return (
      <AccessDeniedError
        pluginId={activePluginId!}
        pluginName={activeManifest?.title || activeManifest?.name}
        reason="disabled"
        deniedPath={location.pathname}
        disabledReason={activeManifest?.migration_note || activeManifest?.disabled_reason}
      />
    );
  }

  // Render all mounted plugins using Activity (one visible, others hidden but mounted)
  const mountedList = Array.from(mountedPlugins.entries());
  
  // Debug: log what we're about to render
  console.log(`[PluginActivityRouter] Rendering ${mountedList.length} plugins, active: ${activePluginId}, plugins: [${mountedList.map(([id]) => id).join(', ')}]`);

  return (
    <>
      {mountedList.map(([pluginId, mounted]) => {
        const isActive = pluginId === activePluginId;
        const matched = isActive ? getMatchedRoute(mounted, location.pathname) : null;

        // Handle redirect routes — navigate to target without rendering plugin
        if (isActive && matched?.redirectTo) {
          return <RouteRedirector key={pluginId} to={matched.redirectTo} />;
        }

        // For active plugin without matched route, check authorization
        if (isActive && !matched) {
          const { routes, authorizedRoutes } = mounted;
          const currentPath = location.pathname;
          const attemptedRoute = findAttemptedRoute(routes, currentPath);

          if (attemptedRoute && attemptedRoute.resource && attemptedRoute.action && !authorizedRoutes.has(attemptedRoute.path)) {
            const requestableRoles = extractRequestableRoles(mounted.manifest, pluginId, attemptedRoute.path);
            const accessCatalogManifest = manifests.get('access-catalog');
            return (
              <AccessDeniedError
                key={pluginId}
                pluginId={pluginId}
                pluginName={mounted.manifest.title || mounted.manifest.name}
                reason="pdp"
                requestableRoles={requestableRoles}
                deniedPath={currentPath}
                denialContext={mounted.denialContexts?.get(attemptedRoute.path)}
                hasAccessCatalog={!!accessCatalogManifest && accessCatalogManifest.enabled !== false}
              />
            );
          }

          // No route found at all
          if (!attemptedRoute && currentPath !== `/${pluginId}`) {
            return (
              <div key={pluginId} style={{ padding: '24px', textAlign: 'center' }}>
                <h2>404 - Route Not Found</h2>
                <p>No route found in plugin {pluginId} for path: {currentPath}</p>
                <p>Available routes:</p>
                <ul style={{ textAlign: 'left', display: 'inline-block' }}>
                  {routes.map(r => (
                    <li key={r.path}>{r.path}</li>
                  ))}
                </ul>
              </div>
            );
          }
        }

        // Get component for hidden plugins (use first route)
        let Component = matched?.Component;
        let routeParams = matched?.params || {};
        if (!isActive) {
          const firstRoute = mounted.routes[0];
          Component = firstRoute ? mounted.plugin.routes?.[firstRoute.component] : undefined;
          routeParams = {};
        }

        if (!Component) {
          if (mounted.plugin.class2?.sandboxUrl && isActive) {
            return (
              <ActivityWrapper key={pluginId} mode="visible" pluginId={pluginId}>
                <PluginPageRoot>
                  <Class2PluginFrame
                    pluginId={pluginId}
                    sandboxUrl={mounted.plugin.class2.sandboxUrl}
                    title={mounted.manifest.title || mounted.manifest.name}
                  />
                </PluginPageRoot>
              </ActivityWrapper>
            );
          }
          if (isActive) {
            const { routes, authorizedRoutes } = mounted;
            const attemptedRoute = findAttemptedRoute(routes, location.pathname);
            if (attemptedRoute && attemptedRoute.resource && attemptedRoute.action && !authorizedRoutes.has(attemptedRoute.path)) {
              const requestableRoles = extractRequestableRoles(mounted.manifest, pluginId, attemptedRoute.path);
              const accessCatalogManifest = manifests.get('access-catalog');
              return (
                <AccessDeniedError
                  key={pluginId}
                  pluginId={pluginId}
                  pluginName={mounted.manifest.title || mounted.manifest.name}
                  reason="pdp"
                  requestableRoles={requestableRoles}
                  deniedPath={location.pathname}
                  denialContext={mounted.denialContexts?.get(attemptedRoute.path)}
                  hasAccessCatalog={!!accessCatalogManifest && accessCatalogManifest.enabled !== false}
                />
              );
            }
            if (attemptedRoute && authorizedRoutes.has(attemptedRoute.path)) {
              return (
                <div key={pluginId} style={{ padding: '24px', color: '#ff4444' }}>
                  <h2 style={{ marginTop: 0 }}>Route component not found</h2>
                  <p>
                    Route <code>{attemptedRoute.path}</code> is authorized but component{' '}
                    <code>{attemptedRoute.component}</code> is not registered in plugin{' '}
                    <code>{pluginId}</code>.
                  </p>
                  <p style={{ fontSize: '13px', color: '#888' }}>
                    Available: {Object.keys(mounted.plugin.routes || {}).join(', ') || 'none'}
                  </p>
                </div>
              );
            }
          }
          return null;
        }

        const discoveryConfig = mounted.manifest?.config?.systemDiscovery;
        const pa = _pa();
        const SSProvider = discoveryConfig ? pa?.SystemSelectionProvider : null;
        const SSSwitcher = discoveryConfig ? pa?.SystemSwitcher : null;

        const pluginContent = (
          <PluginErrorSuppressor active={isActive}>
            <Component {...routeParams} />
          </PluginErrorSuppressor>
        );

        return (
          <ActivityWrapper key={pluginId} mode={isActive ? 'visible' : 'hidden'} pluginId={pluginId}>
            <PluginErrorBoundary>
              <PluginProvider
                value={{
                  pluginId: mounted.plugin.id,
                  manifest: mounted.manifest,
                  sdk: mounted.plugin.sdk!,
                }}
              >
                <PluginPageRoot>
                  {SSProvider ? (
                    <SSProvider
                      pluginId={mounted.plugin.id}
                      sdk={mounted.plugin.sdk!}
                      discoveryConfig={discoveryConfig}
                    >
                      {SSSwitcher && (
                        <div
                          data-plugin-system-switcher
                          style={{
                            width: '100%',
                            padding: '8px 32px 0',
                            boxSizing: 'border-box',
                            flexShrink: 0,
                          }}
                        >
                          <SSSwitcher />
                        </div>
                      )}
                      <SystemReadyGate>{pluginContent}</SystemReadyGate>
                    </SSProvider>
                  ) : (
                    pluginContent
                  )}
                </PluginPageRoot>
              </PluginProvider>
            </PluginErrorBoundary>
          </ActivityWrapper>
        );
      })}

      {/* Show message if no plugin matched */}
      {activePluginId && !mountedPlugins.has(activePluginId) && loadingPlugin !== activePluginId && (
        <div style={{ padding: '24px', textAlign: 'center' }}>
          <h2>Plugin Not Found</h2>
          <p>The plugin "{activePluginId}" is not available.</p>
        </div>
      )}

      {/* Debug panel */}
      {debug && (
        <div
          style={{
            position: 'fixed',
            bottom: 10,
            left: 10,
            padding: '8px 12px',
            background: 'rgba(0, 0, 0, 0.8)',
            color: '#00ff00',
            fontFamily: 'monospace',
            fontSize: 11,
            borderRadius: 4,
            zIndex: 99999,
            maxWidth: 300,
          }}
        >
          <div style={{ fontWeight: 'bold', marginBottom: 4 }}>
            🎯 Activity Router
          </div>
          <div>Mounted: {mountedPlugins.size}/{MAX_MOUNTED_PLUGINS}</div>
          <div>Active: {activePluginId || 'none'}</div>
          <div style={{ fontSize: 9, marginTop: 4, opacity: 0.7 }}>
            {Array.from(mountedPlugins.keys()).join(', ')}
          </div>
        </div>
      )}
    </>
  );
}

/**
 * Helper component to suppress errors during plugin initialization
 */
function PluginErrorSuppressor({ 
  active, 
  children 
}: { 
  active: boolean; 
  children: React.ReactNode;
}) {
  useEffect(() => {
    if (active && typeof window !== 'undefined') {
      (window as any).__pluginSuppressErrors = true;
      const timer = setTimeout(() => {
        (window as any).__pluginSuppressErrors = false;
      }, 2000);
      return () => {
        clearTimeout(timer);
        (window as any).__pluginSuppressErrors = false;
      };
    }
  }, [active]);

  return <>{children}</>;
}

export default PluginActivityRouter;

// Export helper functions for testing
export const __testing = {
  getPluginIdFromPath,
  routeMatchesPath,
  findAttemptedRoute,
  pluginOwnsPathSegment,
  activityHostVisibleStyle,
  activityHostHiddenStyle,
};

/**
 * Get mount metrics for observability and debugging.
 * Useful for monitoring race condition prevention in production.
 * 
 * @example
 * // In browser console:
 * import { getMountMetrics } from './PluginActivityRouter';
 * console.log(getMountMetrics());
 * // { duplicatesPrevented: 2, totalMounts: 15, loadingPlugins: ['agent-designer'] }
 */
export function getMountMetrics() {
  return {
    ...mountMetrics,
    loadingPlugins: Array.from(loadingPluginsSet),
    cachedPlugins: Array.from(mountedPluginsCache.keys()),
  };
}

// Expose metrics globally for debugging in production (browser console)
if (typeof window !== 'undefined') {
  (window as any).__pluginActivityRouterMetrics = getMountMetrics;
}

